<script>
import Colours from '@/shared/constants/design/Colours'
import Typography from '@/shared/constants/design/Typography'
import TransitionName from '@/shared/constants/core/TransitionName'

export default {
  name: 'CandidatePreferenceClientItem',
  props: {
    client: {
      type: Object,
      required: true,
      validator: (client) =>
        typeof client.clientId === 'number' &&
        Array.isArray(client.locations) &&
        client.locations.length > 0,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    isSavingPreferences: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      Colours,
      Typography,
      preferredCountTransition: TransitionName.fadeCycleDown,
    }
  },
  computed: {
    hasSingleLocation() {
      return this.client.locations.length === 1
    },
    totalLocationCount() {
      return this.client.locations.length
    },
    preferredLocationCount() {
      return this.client.locations.filter((location) => location.isPreferred)
        .length
    },
    // Reflects every visible location, including disabled ones
    isFullyPreferred() {
      return (
        this.totalLocationCount > 0 &&
        this.preferredLocationCount === this.totalLocationCount
      )
    },
    isPartiallyPreferred() {
      return (
        this.preferredLocationCount > 0 &&
        this.preferredLocationCount < this.totalLocationCount
      )
    },
    isClientPreferred() {
      return (
        !this.client.isDisabled &&
        (this.isFullyPreferred || this.isPartiallyPreferred)
      )
    },
    singleLocationSubtitle() {
      const locationName = this.client.locations[0]?.locationName

      const trimmedLocationName = locationName?.trim()

      return (
        trimmedLocationName ||
        this.$tc(
          'candidateList.preferenceModal.locationCount',
          this.totalLocationCount,
          { count: this.totalLocationCount }
        )
      )
    },
    // Multi-location rows always control expansion, preference toggled via checkbox
    isRowClickable() {
      if (this.isSavingPreferences) return false

      return !this.hasSingleLocation || !this.client.isDisabled
    },
  },
  watch: {
    preferredLocationCount(preferredLocationCount, previousPreferredCount) {
      const { fadeCycleDown, fadeCycleUp } = TransitionName

      this.preferredCountTransition =
        preferredLocationCount > previousPreferredCount
          ? fadeCycleDown
          : fadeCycleUp
    },
  },
  methods: {
    handleClientToggle() {
      this.$emit('toggle-client', this.client)
    },
    handleClientRowClick() {
      if (!this.isRowClickable) return

      if (this.hasSingleLocation) {
        this.handleClientToggle()
        return
      }

      this.handleToggleExpanded()
    },
    handleLocationToggle(location) {
      this.$emit('toggle-location', this.client, location)
    },
    handleLocationRowClick(location) {
      if (this.client.isDisabled || location.isDisabled) return
      this.handleLocationToggle(location)
    },
    handleToggleExpanded() {
      this.$emit('toggle-expanded', this.client.clientId)
    },
  },
}
</script>

<template>
  <li
    class="preference-client"
    :class="{ 'neutrals-grey-100': client.isDisabled }"
  >
    <div
      v-ripple="isRowClickable"
      class="client-preference-row d-flex align-center px-lg p-relative transition-bg"
      :class="{
        'client-preference-row--disabled': client.isDisabled,
        'client-preference-row--interactive': isRowClickable,
        'client-preference-row--selected': isClientPreferred,
        'cursor-pointer': isRowClickable,
      }"
      @click="handleClientRowClick"
    >
      <v-tooltip v-if="client.isDisabled" top>
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            v-bind="attrs"
            size="18"
            color="grey"
            class="client-preference-lock-icon mr-md flex-shrink-0"
            v-on="on"
          >
            mdi-information
          </v-icon>
        </template>
        <span>{{ $t('candidateList.preferenceModal.contactConsultant') }}</span>
      </v-tooltip>
      <v-checkbox
        v-else
        :input-value="isFullyPreferred"
        :indeterminate="isPartiallyPreferred"
        dense
        hide-details
        class="ma-0 mr-md pa-0 flex-shrink-0 client-preference-checkbox"
        color="primary"
        :aria-label="
          $t('candidateList.preferenceModal.toggleClient', {
            name: client.clientName,
          })
        "
        @change="handleClientToggle"
        @click.stop
      />

      <div class="client-preference-summary flex-grow-1">
        <p
          class="client-preference-name text-truncate mb-0"
          :class="[
            Typography.textMdBold,
            { [Colours.text.neutralsGrey400]: client.isDisabled },
          ]"
        >
          {{ client.clientName }}
        </p>
        <p
          class="client-preference-subtitle d-flex align-center mt-3xs mb-0"
          :class="[Typography.textSm, Colours.text.neutralsGrey500]"
        >
          <v-icon size="14" color="grey" class="client-preference-pin">
            mdi-map-marker-outline
          </v-icon>
          <i18n
            v-if="!hasSingleLocation"
            path="candidateList.preferenceModal.locationsPreferredCount"
            tag="span"
            class="text-truncate"
          >
            <template v-slot:preferred>
              <transition :name="preferredCountTransition" mode="out-in">
                <span
                  :key="preferredLocationCount"
                  class="preferred-location-count d-inline-block"
                >
                  {{ preferredLocationCount }}
                </span>
              </transition>
            </template>
            <template v-slot:total>
              <span>{{ totalLocationCount }}</span>
            </template>
          </i18n>
          <span v-else class="text-truncate">
            {{ singleLocationSubtitle }}
          </span>
        </p>
      </div>

      <BaseButton
        v-if="!hasSingleLocation"
        :disabled="isSavingPreferences"
        icon="mdi-chevron-down"
        fab
        text
        sm
        border-radius="6px"
        hover-color="grey lighten-4"
        class="flex-shrink-0 preference-client__chevron"
        :class="{ 'preference-client__chevron--expanded': expanded }"
        :aria-expanded="expanded"
        :aria-controls="`preference-locations-${client.clientId}`"
        :aria-label="
          $t(
            expanded
              ? 'candidateList.preferenceModal.collapseClient'
              : 'candidateList.preferenceModal.expandClient',
            { name: client.clientName }
          )
        "
        @click.stop="handleToggleExpanded"
      />
      <div v-else class="client-preference-chevron-spacer flex-shrink-0" />
    </div>

    <v-expand-transition>
      <div
        v-if="!hasSingleLocation"
        v-show="expanded"
        :id="`preference-locations-${client.clientId}`"
        class="client-preference-locations-wrapper overflow-hidden"
      >
        <ul class="client-preference-locations d-flex flex-column pa-0 ma-0">
          <li
            v-for="location in client.locations"
            :key="`preference-location-${location.locationId}`"
            v-ripple="!(client.isDisabled || location.isDisabled)"
            class="client-preference-location d-flex align-center pr-lg transition-bg"
            :class="{
              'client-preference-location--client-disabled': client.isDisabled,
              'cursor-pointer': !(client.isDisabled || location.isDisabled),
              'client-preference-location--interactive': !(
                client.isDisabled || location.isDisabled
              ),
            }"
            @click="handleLocationRowClick(location)"
          >
            <v-checkbox
              :input-value="location.isPreferred"
              :disabled="location.isDisabled || client.isDisabled"
              dense
              hide-details
              class="ma-0 mr-md pa-0 flex-shrink-0 client-preference-checkbox client-preference-checkbox--location"
              color="primary"
              :aria-label="
                $t('candidateList.preferenceModal.toggleLocation', {
                  name: location.locationName,
                })
              "
              @change="handleLocationToggle(location)"
              @click.stop
            />
            <span
              class="client-preference-location-name text-truncate"
              :class="[
                Typography.textMd,
                { [Colours.text.neutralsGrey400]: location.isDisabled },
              ]"
            >
              {{ location.locationName }}
            </span>
          </li>
        </ul>
      </div>
    </v-expand-transition>
  </li>
</template>
